import { createSelector } from 'reselect';

import {
  STANDARD_ROUND_STATUSES,
  ONBOARDING_STATUSES,
  MASTERED_LEVEL_THRESHOLD,
  QUESTIONS_PER_ROUND,
  DESK_WEIGHT_LIMIT,
} from '../foodTruckConstants';

export const isRoundMastered = createSelector(
  ({ standardRound }) => standardRound.progress,
  progress => progress >= MASTERED_LEVEL_THRESHOLD,
);

export const selectCurrentLevelKey = createSelector(
  [
    ({ standardRound }) => standardRound.levelKey,
    ({ navigation }) => navigation.onboardingMode,
  ],
  (levelKey, onboardingMode) => (onboardingMode ? 'bitsAndBites' : levelKey),
);

export const selectCurrentLevelNumber = createSelector(
  [
    ({ curriculum }) => curriculum.currentLevelId,
  ],
  currentLevelId => (currentLevelId === null ? null : currentLevelId + 1),
);

export const isRoundStarted = createSelector(
  ({ standardRound }) => standardRound.status,
  currentLevelStatus => currentLevelStatus === STANDARD_ROUND_STATUSES.STARTED,
);

export const isOnboardingInterrupted = createSelector(
  ({ standardRound }) => standardRound.onboardingStatus,
  currentOnboardingStatus => currentOnboardingStatus === ONBOARDING_STATUSES.INTERRUPTED,
);

export const isRoundInterrupted = createSelector(
  ({ standardRound }) => standardRound.status,
  currentLevelStatus => currentLevelStatus === STANDARD_ROUND_STATUSES.INTERRUPTED,
);

export const selectCurrentQuestion = createSelector(
  [
    ({ standardRound }) => standardRound.questions,
    ({ standardRound }) => standardRound.currentQuestionIndex,
  ],
  (questions, currentQuestionIndex) => questions[currentQuestionIndex],
);

const getPercentage = (number, total) => Math.round(number / total * 100);

export const selectRoundProgressPercentage = createSelector(
  [
    ({ standardRound }) => standardRound.currentQuestionIndex,
    ({ question }) => question.isAnswerCorrect,
    ({ navigation }) => navigation.instructionMode,
  ],
  (questionIndex, isAnswerCorrect, instructionMode) => {
    const isNextQuestion = instructionMode ? isAnswerCorrect !== null : isAnswerCorrect;

    return getPercentage(isNextQuestion ? questionIndex + 1 : questionIndex, QUESTIONS_PER_ROUND);
  },
);

export const selectTiles = createSelector(
  [
    ({ question }) => question.trayCollection,
  ],
  trayCollection => ({
    left: trayCollection.filter(item => item.left),
    right: trayCollection.filter(item => item.right),
  }),
);

const isOnboardingTileDraggable = (deskTilesAmount, cutAttemptsUsed, tile, isDeskTile = false) => {
  let isDraggable = false;

  // initial "ob" tile is draggable only if there is no tiles on the desk yet
  if (tile.text === 'ob') {
    isDraggable = !deskTilesAmount;
  }
  // initial "at" tile is draggable only if there was 1 cut attempt and there is 1 tile ("b") on the desk
  if (tile.text === 'at') {
    isDraggable = cutAttemptsUsed === 1 && deskTilesAmount === 1;
  }
  // new "o" tile is draggable if there are 2 tiles on the desk ("o" and "b") and "o" is on the desk
  if (tile.text === 'o') {
    isDraggable = deskTilesAmount === 2 && isDeskTile;
  }
  // new "b" tile is never draggable
  if (tile.text === 'b') {
    isDraggable = false;
  }

  return isDraggable;
};

export const selectTilesDraggableSelector = createSelector(
  [
    ({ navigation }) => navigation.onboardingMode,
  ],
  onboardingMode => (onboardingMode ? areOnboardingTilesDraggable : areStandardTilesDraggable),
);

export const areStandardTilesDraggable = createSelector(
  [
    ({ question }) => question.trayCollection,
    ({ question }) => question.deskCollection,
    ({ question }) => question.answer,
    ({ question }) => question.tryAgainModeOn,
  ],
  (trayCollection, deskCollection, answer, tryAgainModeOn) => {
    const tilesObject = {};

    [...trayCollection, ...deskCollection].forEach((tile) => {
      // for standard mode tile is draggable if there is no answer provided by user and try again mode is off
      tilesObject[tile.text] = !answer && !tryAgainModeOn;
    });

    return tilesObject;
  },
);

export const areOnboardingTilesDraggable = createSelector(
  [
    ({ question }) => question.trayCollection,
    ({ question }) => question.deskCollection,
    ({ question }) => question.deskTilesAmount,
    ({ question }) => question.cutAttemptsUsed,
  ],
  (trayCollection, deskCollection, deskTilesAmount, cutAttemptsUsed) => {
    const tilesObject = {};

    trayCollection.forEach((tile) => {
      // for onboarding mode we decide for each tile separately whether it's draggable or not
      tilesObject[tile.text] = isOnboardingTileDraggable(deskTilesAmount, cutAttemptsUsed, tile);
    });

    deskCollection.forEach((tile) => {
      tilesObject[tile.text] = isOnboardingTileDraggable(deskTilesAmount, cutAttemptsUsed, tile, true);
    });

    return tilesObject;
  },
);

export const isOnboardingCompleted = createSelector(
  [
    ({ selectedActivity }) => selectedActivity.isQuestWorkflow,
    ({ foodTruck }) => foodTruck.gameDetails.isQuestOnboardingCompleted,
    ({ foodTruck }) => foodTruck.gameDetails.isPracticeOnboardingCompleted,
  ],
  (isQuestWorkflow, isQuestOnboardingCompleted, isPracticeOnboardingCompleted) =>
    (isQuestWorkflow ? isQuestOnboardingCompleted : isPracticeOnboardingCompleted),
);

// power plates tiles (level 2) can be split only by onset-time chunks
export const splitPowerPlatesTile = (text) => {
  let tiles = [text];

  if (text === 'stop') {
    tiles = ['st', 'op'];
  } else if (text === 'spin') {
    tiles = ['sp', 'in'];
  } else if (text.length > 3) {
    tiles = [
      text.slice(0, text.length - 3),
      text.slice(text.length - 3),
    ];
  }

  return tiles;
};

// level 1 and 3 tiles can be split anywhere
export const splitCasualTile = text => text.split('');

export const selectTilesLetters = createSelector(
  [
    ({ navigation }) => navigation.standardMode,
    ({ standardRound }) => standardRound.levelKey,
    ({ question }) => question.deskCollection,
  ],
  (standardMode, levelKey, deskCollection) => {
    const tilesObject = {};

    deskCollection.forEach((tile) => {
      tilesObject[tile.text] = standardMode && levelKey === 'powerPlates' ?
        splitPowerPlatesTile(tile.text) : splitCasualTile(tile.text);
    });

    return tilesObject;
  },
);

export const isResetButtonAvailable = createSelector(
  [
    ({ navigation }) => navigation.standardMode,
    ({ question }) => question.answer,
    ({ question }) => question.deskTilesAmount,
  ],
  (standardMode, answer, deskTilesAmount) => standardMode && !answer && deskTilesAmount > 0,
);

export const isServeButtonAvailable = createSelector(
  [
    ({ navigation }) => navigation.onboardingMode,
    ({ navigation }) => navigation.instructionMode,
    ({ question }) => question.answer,
    ({ question }) => question.deskTilesAmount,
    ({ question }) => question.cutTilesOffDeskAmount,
    ({ question }) => question.initialCollection,
  ],
  (onboardingMode, instructionMode, answer, deskTilesAmount, cutTilesOffDeskAmount, initialCollection) => {
    let isAvailable;

    if (onboardingMode) {
      isAvailable = cutTilesOffDeskAmount > 0 && deskTilesAmount === 2;
    } else if (instructionMode) {
      isAvailable = deskTilesAmount === initialCollection.length;
    } else {
      isAvailable = deskTilesAmount > 1;
    }

    return !answer && isAvailable;
  },
);

export const isKnifeButtonAvailable = createSelector(
  [
    ({ navigation }) => navigation.onboardingMode,
    ({ navigation }) => navigation.instructionMode,
    ({ question }) => question.answer,
    ({ question }) => question.deskTilesAmount,
    ({ question }) => question.cutAttemptsUsed,
  ],
  (onboardingMode, instructionMode, answer, deskTilesAmount, cutAttemptsUsed) => {
    let isAvailable;

    if (onboardingMode) {
      isAvailable = !answer && deskTilesAmount > 0 && !cutAttemptsUsed;
    } else if (instructionMode) {
      isAvailable = false;
    } else {
      isAvailable = !answer && deskTilesAmount > 0;
    }

    return isAvailable;
  },
);

export const isDeskFull = createSelector(
  [
    ({ animation }) => (animation.activeTileText ? animation.activeTileText.length : 0),
    ({ question }) => question.deskWeight,
  ],
  (activeTileWeight, deskWeight) => (activeTileWeight + deskWeight > DESK_WEIGHT_LIMIT),
);

export const selectDeskHash = createSelector(
  [
    ({ question }) => question.deskCollection,
  ],
  deskCollection => deskCollection.reduce(
    (text, tile) => `${text}${text ? '-' : ''}${tile.text}`,
    '',
  ),
);
